import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import styled from 'styled-components/native';
import MyDataList from '../../components/MyDataList';
import Colors from '../../constants/Colors';
import { useUserContext } from '../../context/UserContext';
import { UserAttributeSource } from '../../models/UserAttribute';
import {
  useNavigation,
  useFocusEffect,
  useRoute,
  RouteProp
} from '@react-navigation/core';
import { StackNavigationProp } from '@react-navigation/stack';
import {
  ActivityIndicator,
  Platform,
  TouchableOpacity,
  RefreshControl
} from 'react-native';
import {
  DeviceSyncStatus,
  useDeviceSyncContext
} from '../../context/DeviceSyncContext';
import MeasurementsActionSheet from '../../components/modals/MeasurementsActionSheet';
import { H5, P, Subtitle } from '../../components/Typography';
import { Ionicons, Feather } from '@expo/vector-icons';
import Spacer from '../../components/Spacer';
import {
  gActiveStudyParticipant,
  useHasActiveStudy
} from '../../hooks/study/useHasActiveStudy';
import AlertBox from '../../components/AlertBox';
import { defaultScreenComponentPlugins } from '../study/screen-components/plugins/default';
import ActivityIndicatorScreen from '../../components/ActivityIndicatorScreen';
import { log } from '../../tools/LogHelper';
import { MyDataParamList } from '../../navigation/MyDataNav';
import { useUserAttributesContext } from '../../context/UserAttributesContext';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { SelectedWearableConfig } from '../study/screen-components/plugins/WearableDataLineChartCardPlugin';
import elementIDs from '../../../elementIDs';

const StyledScrollView = styled.ScrollView`
  width: 100%;
  height: 100%;
  background-color: ${Colors.background};
`;

const StyledStudyTitle = styled(H5)`
  margin-bottom: 22px;
`;

const StyledStudyComponentsContainer = styled.View`
  width: 100%;
  padding: 0px 0px;
`;

const StyledContainer = styled.View`
  flex: 1;
  width: 100%;
  padding: 0 21px 24px 21px;
  align-items: center;
`;

const StyledView = styled.View`
  flex: 1;
  width: 100%;
  max-width: 768px;
`;

const StyledHeaderContainer = styled.View`
  flex-direction: row;
  align-items: center;
`;

const StyledGeneralErrorContainer = styled.View`
  min-height: 58px;
  border-radius: 4px;
  border-color: #ffbacf;
  border-width: 1px;
  background-color: #ffeaf4;
  padding: 6px 12px;
  flex-direction: row;
  align-items: center;
`;

const StyledGeneralErrorText = styled(Subtitle)`
  flex: 1;
  margin: 0 0 0 20px;
`;

const StyledTitleContainer = styled.View`
  flex-direction: row;
  flex: 1;
  height: 22px;
`;

const MyDataListScreen: React.FC = () => {
  const navigation = useNavigation<StackNavigationProp<any>>();
  const route = useRoute<RouteProp<MyDataParamList, 'MyDataListScreen'>>();
  const {
    user,
    loadingUser,
    allOccurrences,
    studyParticipants,
    loadingStudyParticipants,
    updateUserSession
  } = useUserContext();
  const hasActiveStudy = useHasActiveStudy();
  const {
    latestAttributes,
    loadingLatestAttributes,
    userAttributesError,
    synchronizingData,
    connectedWearableSources,
    syncData,
    checkConnectedWearables
  } = useUserAttributesContext();
  const { deviceSyncInfo } = useDeviceSyncContext();
  const [showActionSheet, setShowActionSheet] = useState<boolean>();
  const [selectedWearableConfigs, setSelectedWearableConfigs] = useState<
    SelectedWearableConfig[]
  >([]);

  // get current study my data components
  const { activeStudyParticipant, myDataComponents } = useMemo(() => {
    const activeStudyParticipant = gActiveStudyParticipant(
      studyParticipants || [],
      allOccurrences
    );
    const myDataComponents = activeStudyParticipant?.study?.myDataComponents;
    return { activeStudyParticipant, myDataComponents };
  }, [allOccurrences, studyParticipants]);

  const backgroundSyncing = useMemo(
    () =>
      deviceSyncInfo?.status === DeviceSyncStatus.Started ||
      loadingLatestAttributes ||
      loadingUser ||
      loadingStudyParticipants,
    [
      deviceSyncInfo?.status,
      loadingLatestAttributes,
      loadingStudyParticipants,
      loadingUser
    ]
  );

  const error = useMemo(() => {
    let error = userAttributesError?.message;
    if (error) {
      // Log actual error message for debugging purposes
      log('Error: ', error);
      // Convert error message to user-friendly error to be presented to the user
      return 'Unable to retrieve data from the server. Please try to sync again later.';
    }
    if (deviceSyncInfo?.status === DeviceSyncStatus.Failed) {
      // Log actual error message for debugging purposes
      log('Error: ', deviceSyncInfo.deviceKey + ': ' + deviceSyncInfo.message);
      if (
        deviceSyncInfo?.message === 'Fitbit account not connected.' ||
        deviceSyncInfo?.message === 'Fitbit account connection has expired.'
      ) {
        error = 'Please reconnect Fitbit account';
      } else {
        // Convert error message to user-friendly error to be presented to the user
        error = `Failed to sync ${
          deviceSyncInfo.deviceKey === UserAttributeSource.HealthKit
            ? 'Apple Health'
            : deviceSyncInfo.deviceKey
        } data. Please try again shortly`;
      }
    }
    return error;
  }, [
    deviceSyncInfo?.deviceKey,
    deviceSyncInfo?.message,
    deviceSyncInfo?.status,
    userAttributesError?.message
  ]);

  const navigateToWearableIntegration = useCallback(
    () =>
      navigation.navigate('MyDataNav', {
        screen: 'WearableIntegrationScreen',
        params: { disableAutoNavigate: true }
      }),
    [navigation]
  );

  const synchronizeWearableData = useCallback(() => {
    if (user?.id && connectedWearableSources) {
      syncData(user?.id, connectedWearableSources, true);
    }
    setShowActionSheet(false);
  }, [user, connectedWearableSources, syncData]);

  useFocusEffect(
    useCallback(() => {
      checkConnectedWearables();
    }, [checkConnectedWearables])
  );

  useEffect(() => {
    if (user?.id && connectedWearableSources) {
      syncData(user?.id, connectedWearableSources);
    }
  }, [
    connectedWearableSources,
    syncData,
    user?.id,
    user?.fitbitAccessToken,
    user?.ouraAccessToken
  ]);

  useEffect(() => {
    if (
      deviceSyncInfo?.status === DeviceSyncStatus.Failed &&
      (deviceSyncInfo?.message === 'Fitbit account not connected.' ||
        deviceSyncInfo?.message === 'Fitbit account connection has expired.')
    ) {
      updateUserSession();
    }
  }, [deviceSyncInfo?.message, deviceSyncInfo?.status, updateUserSession]);

  useEffect(() => {
    if (
      user?.id &&
      connectedWearableSources &&
      deviceSyncInfo?.status === DeviceSyncStatus.Succeeded &&
      (deviceSyncInfo?.total || 0) > 0
    ) {
      syncData(user.id, connectedWearableSources, undefined, true);
    }
  }, [
    connectedWearableSources,
    deviceSyncInfo?.status,
    deviceSyncInfo?.total,
    syncData,
    user?.id
  ]);

  useFocusEffect(
    useCallback(() => {
      const getWearableConfigs = async () => {
        const wearableConfigs = await AsyncStorage.getItem('wearableConfigs');
        if (wearableConfigs) {
          setSelectedWearableConfigs(JSON.parse(wearableConfigs));
        }
      };
      getWearableConfigs();
    }, [])
  );

  return (
    <StyledScrollView
      contentContainerStyle={{ flexGrow: 1 }}
      refreshControl={
        <RefreshControl
          refreshing={false}
          onRefresh={synchronizeWearableData}
          enabled={!synchronizingData}
        />
      }
      scrollEnabled={!synchronizingData}
    >
      <StyledContainer>
        <StyledView>
          {!synchronizingData &&
            connectedWearableSources &&
            connectedWearableSources.length > 0 &&
            error && (
              <StyledGeneralErrorContainer>
                <Ionicons name='warning-outline' size={24} color='#A10032' />
                <StyledGeneralErrorText color='#A10032'>
                  {error}
                </StyledGeneralErrorText>
              </StyledGeneralErrorContainer>
            )}
          <Spacer />
          <StyledHeaderContainer>
            <StyledTitleContainer>
              <H5 color='dark'>Measurements</H5>
              {!synchronizingData && backgroundSyncing && (
                <ActivityIndicator style={{ marginLeft: 10 }} color='#999' />
              )}
            </StyledTitleContainer>
            {!synchronizingData &&
              !route.params?.isEmbedded &&
              !route.params?.isEmbedded && (
                <TouchableOpacity
                  activeOpacity={0.7}
                  onPress={() => setShowActionSheet(true)}
                >
                  <Feather
                    testID={elementIDs.myDataEllipsisID}
                    name='more-vertical'
                    size={24}
                    color='black'
                  />
                </TouchableOpacity>
              )}
          </StyledHeaderContainer>
          <Spacer size={'large'} />
          {!synchronizingData &&
          backgroundSyncing &&
          (!latestAttributes || latestAttributes?.length === 0) ? (
            <P color='dark' center>
              Hey! 👋 This screen might take a moment to load all of your data.
              Finish up any activities and check back for your data. Thanks for
              your patience!
            </P>
          ) : synchronizingData ? (
            <ActivityIndicatorScreen color='#999' modal />
          ) : (
            <>
              {latestAttributes && (
                <MyDataList
                  wearableConfigs={selectedWearableConfigs}
                  embedded={route.params?.isEmbedded}
                  latestAttributes={latestAttributes}
                  onAddWearableClicked={navigateToWearableIntegration}
                />
              )}
              <Spacer
                size={synchronizingData ? 3 : Platform.OS === 'web' ? 2 : 1}
              />
              {hasActiveStudy &&
                activeStudyParticipant &&
                activeStudyParticipant.startDate &&
                myDataComponents &&
                myDataComponents.length > 0 && (
                  <>
                    <StyledStudyTitle color='dark'>
                      {activeStudyParticipant?.study?.name}
                    </StyledStudyTitle>
                    <StyledStudyComponentsContainer>
                      {myDataComponents.map((component, index) => {
                        const plugin = defaultScreenComponentPlugins.find(
                          p => p.type === component.type
                        );
                        return plugin ? (
                          // can't use component.id due to possible duplicates
                          <Fragment key={index}>
                            <plugin.component
                              connectedWearableSources={
                                connectedWearableSources || []
                              }
                              latestAttributes={latestAttributes || []}
                              embedded={route.params?.isEmbedded}
                              studyParticipant={activeStudyParticipant}
                              component={component}
                              {...(component.props || {})}
                            />
                            <Spacer size={2} />
                          </Fragment>
                        ) : (
                          <AlertBox severity='error'>
                            &quot;{component.name || component.type}&quot;
                            component not found.
                          </AlertBox>
                        );
                      })}
                    </StyledStudyComponentsContainer>
                  </>
                )}
            </>
          )}
          {showActionSheet && user && (
            <MeasurementsActionSheet
              onRefresh={synchronizeWearableData}
              onAddIntegration={() => {
                setShowActionSheet(false);
                navigateToWearableIntegration();
              }}
              onRequestClose={() => setShowActionSheet(false)}
            />
          )}
        </StyledView>
      </StyledContainer>
    </StyledScrollView>
  );
};
export default MyDataListScreen;
